import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { HTTP } from '@utils/http';
import type { PublicReferralCampaign, ReferralPublicConfig, WelcomeCopy } from 'types/referral';

/**
 * Contexte de parrainage détecté pour le filleul atterrissant sur /account.
 *
 * Cycle de vie :
 * 1) Parse les query params (`?campaign=&clientId=` nouveau format) ou
 *    `?referral=<base64>` (legacy).
 * 2) Persiste tout de suite en `sessionStorage` pour survivre à un
 *    redirect SSO (OAuth Mindbody — Annecy notamment) : sans cette
 *    persistance, l'aller-retour SSO repart sur `/account` sans nos
 *    query params et le hero disparaît.
 * 3) Re-hydrate depuis sessionStorage au mount si l'URL ne contient
 *    rien (= retour de SSO).
 * 4) Fetch en arrière-plan :
 *    - la campagne complète via `/referral/active-campaigns` (la
 *      `welcomeCopy` est mergée avec les `welcomeCopyDefaults`)
 *    - le prénom du parrain via `/client/public-info?clientId=…`
 */

export type ReferralMode = 'new' | 'legacy';

/**
 * Erreur détectée côté front sur le lien (token de templating laissé
 * en clair par Stretch / un copier-coller direct du BO).
 */
export type ReferralLinkError = 'unsubstituted_template';

/**
 * Statut résolu côté serveur pour un campaignId donné. 'unknown' est
 * la valeur initiale avant la résolution.
 */
export type CampaignStatus =
  | 'unknown'
  | 'active'
  | 'scheduled'
  | 'expired'
  | 'exhausted'
  | 'inactive'
  | 'not_found';

export interface ReferralContextValue {
  /** true dès qu'un contexte référral est détecté (nouveau ou legacy). */
  hasReferral: boolean;
  /** 'new' = `?campaign=&clientId=`, 'legacy' = `?referral=<base64>`. null si aucun. */
  mode: ReferralMode | null;
  /** Id Mindbody du parrain (extrait du query param OU du legacy base64). */
  referrerClientId: string | null;
  /** Prénom du parrain (résolu en arrière-plan). null = en cours / inconnu. */
  referrerFirstName: string | null;
  /** Id de la campagne (nouveau format uniquement). null en mode legacy. */
  campaignId: string | null;
  /** Campagne complète (chargée depuis l'API publique). null pendant le fetch. */
  campaign: PublicReferralCampaign | null;
  /** Copy mergée campaign.welcomeCopy + welcomeCopyDefaults — toujours non-null quand on a un mode. */
  welcomeCopy: WelcomeCopy | null;
  /** Indique si le fetch de la campagne est encore en cours. */
  isLoadingCampaign: boolean;
  /** Erreur détectée sur le lien (token de templating non remplacé). null sinon. */
  linkError: ReferralLinkError | null;
  /**
   * Statut résolu de la campagne (nouveau format seulement). Permet au
   * front d'afficher une bannière spécifique "pas encore commencée" /
   * "expirée" / "quota atteint". 'active' = le hero peut s'afficher.
   * 'unknown' = pas encore résolu ou mode legacy.
   */
  campaignStatus: CampaignStatus;
  /** Efface le contexte (utile si on veut "skip welcome"). */
  clear: () => void;
}

const SESSION_STORAGE_KEY = 'msc_referral_context';

interface PersistedContext {
  mode: ReferralMode;
  referrerClientId: string;
  campaignId: string | null;
}

function decodeLegacyReferral(raw: string): string | null {
  try {
    const decoded = JSON.parse(atob(raw));
    const id =
      typeof decoded === 'object' && decoded !== null && 'Id' in decoded
        ? (decoded as { Id: unknown }).Id
        : null;
    return id != null ? String(id) : null;
  } catch {
    return null;
  }
}

/**
 * Détecte un token de templating laissé en clair dans l'URL (Stretch,
 * Mailjet, copier-coller direct depuis le BO, etc.) :
 * - n'importe quel marqueur `{…}` ou `{{…}}`, ce qui couvre le `{{id}}`
 *   attendu par Stretch comme tout autre nom de token ;
 * - `CLIENT_ID` / `client_id` / `CLIENT-ID` / `CLIENTID` (avec ou sans
 *   séparateur, casse-insensible), le token proposé par le BO jusqu'au
 *   21/09/2026, qui traîne encore dans d'anciens publipostages.
 *
 * Tout autre clientId (digits, lettres+digits, etc.) passe : Mindbody
 * reste l'autorité finale sur la validité de l'id.
 */
function looksLikeUnsubstitutedTemplate(v: string | null | undefined): boolean {
  if (typeof v !== 'string') return false;
  if (/CLIENT[\s_-]?ID/i.test(v)) return true;
  if (/\{+[^}]*\}+/.test(v)) return true;
  return false;
}

interface ParseResult {
  context: PersistedContext | null;
  linkError: ReferralLinkError | null;
}

function parseUrl(search: string): ParseResult {
  const params = new URLSearchParams(search);
  const campaign = params.get('campaign');
  const clientId = params.get('clientId');

  if (campaign && clientId) {
    if (looksLikeUnsubstitutedTemplate(clientId) || looksLikeUnsubstitutedTemplate(campaign)) {
      // Lien Stretch dont le token n'a pas été substitué côté publipostage.
      // Cas typique : copier-coller direct depuis le BO pour test.
      return { context: null, linkError: 'unsubstituted_template' };
    }
    return {
      context: { mode: 'new', referrerClientId: clientId, campaignId: campaign },
      linkError: null,
    };
  }

  const referral = params.get('referral');
  if (referral) {
    const id = decodeLegacyReferral(referral);
    if (id && !looksLikeUnsubstitutedTemplate(id)) {
      return {
        context: { mode: 'legacy', referrerClientId: id, campaignId: null },
        linkError: null,
      };
    }
  }
  return { context: null, linkError: null };
}

function readSession(): PersistedContext | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      (parsed.mode === 'new' || parsed.mode === 'legacy') &&
      typeof parsed.referrerClientId === 'string' &&
      parsed.referrerClientId !== ''
    ) {
      return parsed as PersistedContext;
    }
  } catch {
    // sessionStorage indisponible (mode privé Safari rare) — ignore.
  }
  return null;
}

function writeSession(ctx: PersistedContext): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    // best-effort
  }
}

function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/**
 * Parrainage en attente de réclamation après connexion.
 *
 * Le contexte d'affichage (hero, onglet, formulaire) reste en
 * `sessionStorage`, limité à l'onglet comme avant. Seul le cas « j'ai déjà un
 * compte » a besoin de survivre à un changement d'onglet (le lien de « mot de
 * passe oublié » s'ouvre ailleurs) : on ne persiste donc en `localStorage`,
 * sous une clé à part et pour peu de temps, que lorsque l'inscription vient de
 * répondre « adresse déjà utilisée » à un visiteur arrivé par un lien. Un
 * simple clic sur un lien ne laisse rien derrière lui sur un poste partagé.
 */
const PENDING_CLAIM_KEY = 'msc_referral_pending_claim';
const PENDING_CLAIM_TTL_MS = 12 * 60 * 60 * 1000;

export interface PendingReferral {
  referrerClientId: string;
  campaignId: string | null;
}

export function rememberPendingReferral(): void {
  const ctx = readSession();
  if (!ctx) return;
  try {
    localStorage.setItem(
      PENDING_CLAIM_KEY,
      JSON.stringify({ referrerClientId: ctx.referrerClientId, campaignId: ctx.campaignId, savedAt: Date.now() }),
    );
  } catch {
    // best-effort
  }
}

function readStoredPendingClaim(): PendingReferral | null {
  try {
    const raw = localStorage.getItem(PENDING_CLAIM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.referrerClientId === 'string' &&
      parsed.referrerClientId !== '' &&
      typeof parsed.savedAt === 'number' &&
      Date.now() - parsed.savedAt < PENDING_CLAIM_TTL_MS
    ) {
      return { referrerClientId: parsed.referrerClientId, campaignId: parsed.campaignId ?? null };
    }
    localStorage.removeItem(PENDING_CLAIM_KEY);
  } catch {
    // localStorage indisponible : rien en attente.
  }
  return null;
}

/**
 * Parrainage à réclamer pour le client qui vient de se connecter : le lien
 * ouvert dans cet onglet, sinon celui mémorisé par l'inscription refusée.
 * Lu dans le stockage et non dans l'état du hook, parce qu'un autre composant
 * a pu le vider entretemps (inscription réussie).
 */
export function readPendingReferral(): PendingReferral | null {
  const ctx = readSession();
  if (ctx) return { referrerClientId: ctx.referrerClientId, campaignId: ctx.campaignId };
  return readStoredPendingClaim();
}

export function clearPendingReferral(): void {
  clearSession();
  try {
    localStorage.removeItem(PENDING_CLAIM_KEY);
  } catch {
    // best-effort
  }
}

function mergeWelcomeCopy(
  defaults: WelcomeCopy | undefined,
  override: Partial<WelcomeCopy> | undefined,
): WelcomeCopy | null {
  if (!defaults && !override) return null;
  const base: WelcomeCopy = defaults ?? {
    title: '',
    subtitle: '',
    offerHeadline: '',
    offerBody: '',
    steps: ['', '', ''],
    heroImageUrl: null,
    heroImageFit: 'cover',
    heroImagePosition: 'center center',
    heroImageVisiblePercent: 100,
    ctaLabel: '',
  };
  if (!override) return base;
  // Merge per-step : un step vide dans l'override doit retomber sur le step
  // correspondant des defaults. Sinon, personnaliser uniquement step 2 ferait
  // écraser steps 1 et 3 par des chaînes vides → tout retomberait sur les
  // fallbacks i18n côté ReferralWelcome (bug remonté par Daphné le 02/06).
  const mergedSteps: [string, string, string] = ([0, 1, 2].map((i) => {
    const o = Array.isArray(override.steps) ? override.steps[i] : undefined;
    if (typeof o === 'string' && o.trim() !== '') return o;
    return base.steps[i] ?? '';
  }) as [string, string, string]);
  return {
    title: override.title ?? base.title,
    subtitle: override.subtitle ?? base.subtitle,
    offerHeadline: override.offerHeadline ?? base.offerHeadline,
    offerBody: override.offerBody ?? base.offerBody,
    steps: mergedSteps,
    heroImageUrl: override.heroImageUrl ?? base.heroImageUrl,
    heroImageFit: override.heroImageFit ?? base.heroImageFit,
    heroImagePosition: override.heroImagePosition ?? base.heroImagePosition,
    heroImageVisiblePercent: override.heroImageVisiblePercent ?? base.heroImageVisiblePercent,
    ctaLabel: override.ctaLabel ?? base.ctaLabel,
  };
}

export function useReferralContext(): ReferralContextValue {
  const { search } = useLocation();

  // Détection initiale : URL > sessionStorage (URL gagne car elle est plus fraîche).
  // En cas de linkError, on clear le sessionStorage pour éviter qu'un ancien
  // contexte stocké lors d'un précédent passage valide ne refasse surface et
  // affiche un hero alors que l'URL actuelle est cassée.
  const { context: initialContext, linkError } = useMemo<{
    context: PersistedContext | null;
    linkError: ReferralLinkError | null;
  }>(() => {
    const fromUrl = parseUrl(search);
    if (fromUrl.linkError) {
      clearSession();
      return { context: null, linkError: fromUrl.linkError };
    }
    if (fromUrl.context) {
      writeSession(fromUrl.context);
      return { context: fromUrl.context, linkError: null };
    }
    return { context: readSession(), linkError: null };
  }, [search]);

  const [welcomeCopyDefaults, setWelcomeCopyDefaults] = useState<WelcomeCopy | null>(null);
  const [campaign, setCampaign] = useState<PublicReferralCampaign | null>(null);
  const [campaignStatus, setCampaignStatus] = useState<CampaignStatus>('unknown');
  const [referrerFirstName, setReferrerFirstName] = useState<string | null>(null);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState<boolean>(!!initialContext);

  // Évite les double-fetch en mode StrictMode (effets joués 2x au mount).
  // `defaultsLoaded` est nécessaire en mode legacy où `campaignId` reste null :
  // sans ce flag, la dédup `campaignId !== null` ferait `null !== null = false`
  // et le fetch /active-campaigns (qui apporte les welcomeCopyDefaults) ne se
  // déclencherait jamais → hero rendu avec des textes vides.
  const fetchedRef = useRef<{
    campaignId: string | null;
    referrerClientId: string | null;
    defaultsLoaded: boolean;
  }>({
    campaignId: null,
    referrerClientId: null,
    defaultsLoaded: false,
  });

  useEffect(() => {
    if (!initialContext) {
      setIsLoadingCampaign(false);
      return;
    }
    let cancelled = false;

    // Quand on a un campaignId, l'endpoint /campaign-status nous donne en
    // un seul aller-retour : la campagne complète (peu importe son statut),
    // le statut résolu (active / scheduled / expired / exhausted / inactive
    // / not_found) ET les welcomeCopyDefaults. Pour le mode legacy on tape
    // sur /active-campaigns juste pour récupérer les defaults — la copy du
    // hero vient donc EXACTEMENT du même endroit (texte d'accueil par défaut
    // configuré dans le BO Parrainage), peu importe le format de lien.
    const fetchCampaign = (() => {
      if (initialContext.campaignId) {
        if (fetchedRef.current.campaignId === initialContext.campaignId) {
          return Promise.resolve();
        }
        return HTTP.get<{
          status: CampaignStatus;
          campaign: PublicReferralCampaign | null;
          welcomeCopyDefaults: WelcomeCopy;
        }>(`/referral/campaign-status/${encodeURIComponent(initialContext.campaignId)}`)
          .send()
          .then((res) => {
            if (cancelled) return;
            setWelcomeCopyDefaults(res.welcomeCopyDefaults);
            setCampaign(res.campaign);
            setCampaignStatus(res.status);
          })
          .catch(() => {
            if (!cancelled) {
              setCampaign(null);
              setCampaignStatus('not_found');
            }
          });
      }
      // Mode legacy — `?referral=<base64>`. Pas de campaignId, mais on veut
      // quand même le hero avec les defaults globaux du BO.
      if (fetchedRef.current.defaultsLoaded) return Promise.resolve();
      return HTTP.get<ReferralPublicConfig>('/referral/active-campaigns')
        .send()
        .then((cfg) => {
          if (cancelled) return;
          setWelcomeCopyDefaults(cfg.welcomeCopyDefaults);
          setCampaign(null);
          setCampaignStatus('unknown'); // legacy mode
        })
        .catch(() => {
          if (!cancelled) {
            setCampaign(null);
            setCampaignStatus('unknown');
          }
        });
    })();

    const fetchReferrer =
      fetchedRef.current.referrerClientId !== initialContext.referrerClientId
        ? HTTP.get<{ firstName: string | null }>('/client/public-info', {
            clientId: initialContext.referrerClientId,
          })
            .send()
            .then((res) => {
              if (!cancelled) setReferrerFirstName(res?.firstName ?? null);
            })
            .catch(() => {
              if (!cancelled) setReferrerFirstName(null);
            })
        : Promise.resolve();

    Promise.allSettled([fetchCampaign, fetchReferrer]).finally(() => {
      if (!cancelled) {
        fetchedRef.current = {
          campaignId: initialContext.campaignId,
          referrerClientId: initialContext.referrerClientId,
          defaultsLoaded: true,
        };
        setIsLoadingCampaign(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [initialContext]);

  const welcomeCopy = useMemo(() => {
    if (!initialContext) return null;
    return mergeWelcomeCopy(welcomeCopyDefaults ?? undefined, campaign?.welcomeCopy);
  }, [initialContext, welcomeCopyDefaults, campaign]);

  return {
    hasReferral: !!initialContext,
    mode: initialContext?.mode ?? null,
    referrerClientId: initialContext?.referrerClientId ?? null,
    referrerFirstName,
    campaignId: initialContext?.campaignId ?? null,
    campaign,
    welcomeCopy,
    isLoadingCampaign,
    linkError,
    campaignStatus,
    clear: () => {
      clearSession();
      setCampaign(null);
      setCampaignStatus('unknown');
      setReferrerFirstName(null);
      setWelcomeCopyDefaults(null);
    },
  };
}
